import React from 'react';
import { CircularProgress, Tooltip as MuiTooltip, Chip, Box, Typography } from '@mui/material';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useDoraSummary } from './useDoraSummary';
import { usePRCount } from './useCPData';
import './ArtemyStyles.css';

const sparkData = (vals, key) => vals.map((v, i) => ({ i, [key]: v }));

export default function CPDoraMetrics() {
  const { dora, loading } = useDoraSummary();
  const { prCount, loading: prLoading } = usePRCount();

  const metrics = [
    { label: 'Deployment Frequency', value: dora?.deployFreq || '4.2', unit: '/day', color: 'var(--artemy-green)', spark: [2.1, 2.5, 3, 3.5, 3.8, 4, 4.2, 4.2], desc: 'How often code is deployed to production', key: 'Deployment Frequency' },
    { label: 'Lead Time for Changes', value: dora?.leadTime || '2.8', unit: 'days', color: 'var(--artemy-yellow)', spark: [4, 3.8, 3.5, 3.2, 3, 2.9, 2.8, 2.8], desc: 'Time from commit to production', key: 'Lead Time for Changes' },
    { label: 'Change Failure Rate', value: dora?.changeFailRate || '6.1', unit: '%', color: 'var(--artemy-accent)', spark: [14, 13, 11, 10, 9, 8, 7, 6], desc: 'Percentage of deployments causing incidents', key: 'Change Failure Rate' },
    { label: 'Time to Restore Service', value: dora?.mttr || '47', unit: 'min', color: 'var(--artemy-green)', spark: [68, 65, 62, 60, 57, 54, 50, 47], desc: 'Average time to restore service', key: 'Time to Restore Service' },
  ];

  const getFullMetric = (key) => dora?.fullMetrics?.find(m => m["Metric Name"] === key);

  const getCategoryColor = (cat) => {
    switch (cat?.toLowerCase()) {
      case 'elite': return '#6200EA';
      case 'high': return 'var(--artemy-green)';
      case 'medium': return 'var(--artemy-yellow)';
      case 'low': return 'var(--artemy-accent)';
      default: return 'var(--artemy-text-dim)';
    }
  };

  return (
    <div className="artemy-fade-up">
      <div style={{ marginBottom: 22 }}>
        <div className="artemy-page-title">DORA Metrics</div>
        <div className="artemy-page-sub">DevOps Research & Assessment — 4 key delivery performance indicators</div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 32 }}><CircularProgress size={28} sx={{ color: 'var(--artemy-accent)' }} /></div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 16 }}>
            {metrics.map((m) => {
              const full = getFullMetric(m.key);
              const displayValue = full ? full["Metric Value"] : m.value;
              
              return (
                <div key={m.label} className="artemy-card" style={{ padding: 18, transition: 'all .2s', position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 10.5, color: 'var(--artemy-text-secondary)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{m.label}</div>
                    {full?.Category && (
                      <Chip 
                        label={full.Category} 
                        size="small" 
                        sx={{ 
                          height: 16, 
                          fontSize: '8px', 
                          fontWeight: 700, 
                          bgcolor: getCategoryColor(full.Category), 
                          color: '#fff',
                          '& .MuiChip-label': { px: 1 }
                        }} 
                      />
                    )}
                  </div>
                  <MuiTooltip 
                    title={
                      full?.Range ? (
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: 'block' }}>Benchmarks:</Typography>
                          {Object.entries(full.Range).map(([k, v]) => (
                            <Typography key={k} variant="caption" sx={{ display: 'block', fontSize: '10px' }}>
                              <strong style={{ opacity: 0.8 }}>{k}:</strong> {v}
                            </Typography>
                          ))}
                        </Box>
                      ) : ""
                    }
                    arrow
                    placement="top"
                  >
                    <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 22, fontWeight: 700, color: full?.Category ? getCategoryColor(full.Category) : m.color, lineHeight: 1.2, cursor: full?.Range ? 'help' : 'default' }}>
                      {displayValue}
                    </div>
                  </MuiTooltip>
                  <div style={{ marginTop: 10, fontSize: 11, color: 'var(--artemy-text-dim)', lineHeight: 1.4 }}>{m.desc}</div>
                </div>
              );
            })}
          </div>

          {/* Trend bar chart & PRs */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
            <div className="artemy-card" style={{ overflow: 'hidden' }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--artemy-border)' }}>
                <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 14, fontWeight: 600 }}>Pull Request Status</div>
              </div>
              <div style={{ padding: 20 }}>
                {prLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}><CircularProgress size={28} sx={{ color: 'var(--artemy-accent)' }} /></div>
                ) : prCount && (prCount.submitted > 0 || prCount.approved > 0) ? (
                  <div style={{ position: 'relative', height: 180 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Approved', value: prCount.approved, color: 'var(--artemy-green)' },
                            { name: 'Pending/Submitted', value: Math.max(0, prCount.submitted - prCount.approved), color: 'var(--artemy-accent)' },
                          ]}
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                          stroke="none"
                        >
                          <Cell key="Approved" fill="var(--artemy-green)" />
                          <Cell key="Submitted" fill="var(--artemy-accent)" />
                        </Pie>
                        <RechartsTooltip
                          contentStyle={{ background: 'var(--artemy-bg-surface)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }}
                          itemStyle={{ color: '#fff' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'var(--artemy-font-display)', color: 'var(--artemy-text-primary)' }}>{prCount.submitted}</div>
                      <div style={{ fontSize: 10, fontFamily: 'var(--artemy-font-mono)', color: 'var(--artemy-text-secondary)', textTransform: 'uppercase' }}>Total PRs</div>
                    </div>
                  </div>
                ) : (
                  <div style={{ paddingTop: 60, textAlign: 'center', color: 'var(--artemy-text-dim)', fontSize: 13 }}>
                    No PR data available for this pod context.
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      <div style={{ height: 40 }} />
    </div>
  );
}
